import {Props_StatsAlt} from "./StatsAlt.props";
import {CompileMarkdown} from "../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler";
import { motion, animate, useMotionValue, useTransform, useInView } from 'framer-motion';
import React, { useEffect, useState, useRef } from 'react';

// Animated circular progress + counter component
const StatProgress: React.FC<{ valueRaw: string; symbolRaw: string; color?: string }> = ({ valueRaw, symbolRaw, color }) => {
    const valueNum = parseFloat(valueRaw) || 0;
    const symbol = symbolRaw || '';
    const percentValue = symbol.trim() === '%' ? Math.min(valueNum, 100) : valueNum; // cap if percentage

    const [displayNumber, setDisplayNumber] = useState(0);
    const progressMV = useMotionValue(0);
    const radius = 40;
    const circumference = 2 * Math.PI * radius;

    const ref = useRef<HTMLDivElement | null>(null);
    const isInView = useInView(ref, { once: true, margin: '-50px' });

    // Animate numeric display when in view
    useEffect(() => {
        if (!isInView) return;
        const controls = animate(0, valueNum, {
            duration: 1.5,
            ease: 'easeOut',
            onUpdate: v => setDisplayNumber(Math.round(v))
        });
        return () => controls.stop();
    }, [valueNum, isInView]);

    // Animate circular stroke when in view
    useEffect(() => {
        if (!isInView) return;
        const target = symbol.trim() === '%' ? percentValue : Math.min(valueNum, 100);
        const controls = animate(progressMV, target, { duration: 1.5, ease: 'easeOut' });
        return () => controls.stop();
    }, [percentValue, valueNum, symbol, progressMV, isInView]);

    const strokeOffset = useTransform(progressMV, latest => {
        const pct = latest / 100; // treat motion value as percentage 0-100
        return circumference - pct * circumference;
    });

    const progressColor = color || 'currentColor';


    return (
        <div ref={ref} className="stats-alt__stat-progress" style={{ position: 'relative', width: 250, height: 250 }}>
            <svg width={250} height={250} viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
                <circle cx={50} cy={50} r={radius} stroke={progressColor} strokeWidth={4} fill="none" opacity={0.4}/>
                <motion.circle
                    cx={50}
                    cy={50}
                    r={radius}
                    stroke={progressColor}
                    strokeWidth={4}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    style={{ strokeDashoffset: strokeOffset }}
                />
            </svg>
            <div className={`stats-alt__value-container`}>
                <p className="stats-alt__stat-value">
                    {displayNumber}<span>{symbol}</span>
                </p>
            </div>
        </div>
    );
};


const StatsAlternative = (props: Props_StatsAlt ) => {
    const {heading,repeatable_items,highlight} = props // removed highlight
    const classHeadingVisibility = heading.visibility?.value === true ? 'accessible-heading' : '';
    const highlightMap = {
        'covea-dark-blue': '#122147',
        'covea-light-green': '#d6eac1',
        'covea-light-red': '#efded3',
        'covea-light-purple': '#e2d3f2',
        'covea-light-turquoise': '#c8eaed',
        'covea-light-yellow': '#eae8ad',
        'MAAF-dark-blue': '#0091a7',
        'MAAF-light-blue': '#e5f3f5',
        'MAAF-light-red': '#fef6f2',
        'MMA-light-green': '#d6eceb',
        'MMA-light-orange': '#f2e1b5',
        'MMA-dark-blue': '#1d3b6d',
        'GMF-dark-blue': '#144390',
        'GMF-light-blue': '#dfedff',
        'GMF-light-orange': '#fff4ed',
        'Fidelia-dark-green': '#269b38',
        'Fidelia-light-blue': '#e1f0fb',
        'Fidelia-light-green': '#ebf2e7'
    }

    const highlightKey = highlight?.color?.value || 'covea-dark-blue'
    const highlightValue = highlightMap[highlightKey as keyof typeof highlightMap] || highlightMap['covea-dark-blue']

    return (
        <section className={`stats-alt`}>
            <div className={`stats-alt__container`}>
                {heading?.content?.value &&
                    <h2 className={`stats-alt__heading ${classHeadingVisibility}`}>{heading.content?.value}</h2>
                }
                <div className={`stats-alt__stats`}>
                    {repeatable_items.map((item, idx) => (
                        <div className={`stats-alt__stat-item`} key={idx}>
                            <div className={`stats-alt__stat`}>
                                {/* Circular progress + animated number */}
                                <StatProgress valueRaw={String(item?.stat?.value)} symbolRaw={String(item?.symbol?.value)} color={highlightValue} />
                            </div>
                            <div className={`stats-alt__content`}>
                                {CompileMarkdown(item?.content?.value || '')}
                            </div>
                        </div>   
                    ))}
                </div>
            </div>
        </section>
    )
}

export default StatsAlternative;
